import { Check, Link2 } from 'lucide-react';
import { useEffect, useState } from 'react';

interface CopyUrlButtonProps {
  /** URL to copy. Defaults to the current browser URL (kept in sync with the open sheet). */
  url?: string;
  className?: string;
}

/**
 * Copies a shareable URL to the clipboard and shows a brief "Copied!" state.
 * Used inside the portfolio bottom sheets so a reader can share the exact
 * project they're viewing.
 */
const CopyUrlButton = ({ url, className = '' }: CopyUrlButtonProps) => {
  const [copied, setCopied] = useState(false);

  // Reset the confirmation label a couple of seconds after copying.
  useEffect(() => {
    if (!copied) return;
    const timeout = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [copied]);

  const handleCopy = async () => {
    const shareUrl = url ?? window.location.href;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
    } catch {
      // Fallback for browsers/contexts without the async clipboard API.
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
      } catch {
        /* ignore — clipboard unavailable */
      }
      document.body.removeChild(textarea);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label="Copy shareable URL"
      className={`flex items-center gap-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-full px-4 py-2 hover:bg-gray-100 brand-transition ${className}`}
    >
      {copied ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : (
        <Link2 className="h-4 w-4" />
      )}
      {copied ? 'Copied!' : 'Copy URL'}
    </button>
  );
};

export default CopyUrlButton;
